import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MessageCircle } from 'lucide-react';
import { submitSimpleLead } from '../api';
import { toast } from 'sonner';

export const SimpleContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { whatsapp_url }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await submitSimpleLead(formData);
      setSuccess({ whatsapp_url: result.whatsapp_url });
      toast.success("Mensagem enviada. Obrigado!");
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="simple-contact-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-16 text-center">
            <span className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium" data-testid="simple-form-label">
              Contacto rápido
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-stone-900 mt-4 leading-tight italic font-serif uppercase tracking-tight" data-testid="simple-form-title">
              Vamos conversar
            </h2>
            <p className="text-stone-600 font-light mt-6 max-w-md mx-auto italic">
              Deixe-me uma mensagem e respondo o mais breve possível.
            </p>
          </div>

          {success ? (
            <div className="text-center space-y-6 py-12 border-t border-b border-stone-200" data-testid="simple-success">
              <p className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium">Recebido</p>
              <h3 className="text-3xl font-light text-stone-900">
                Obrigado pelo seu contacto.
              </h3>
              <p className="text-stone-600 font-light max-w-md mx-auto">
                A sua mensagem foi guardada. Se quiser uma resposta ainda mais rápida, fale comigo diretamente no WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-4">
                <a
                  href={success.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-button-simple"
                  className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white px-6 py-4 text-sm font-medium tracking-wide rounded-none transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Continuar no WhatsApp
                </a>
                <button
                  onClick={() => setSuccess(null)}
                  className="text-sm text-stone-500 hover:text-stone-900 underline-offset-4 hover:underline transition-colors"
                  data-testid="simple-send-another"
                >
                  Enviar outra mensagem
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="simple-contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-xs tracking-wider uppercase text-stone-500 font-medium">Nome</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required data-testid="simple-name-input"
                    className="mt-3 border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus:border-stone-900 bg-transparent" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs tracking-wider uppercase text-stone-500 font-medium">Telefone</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required data-testid="simple-phone-input"
                    className="mt-3 border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus:border-stone-900 bg-transparent" />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-xs tracking-wider uppercase text-stone-500 font-medium">Email</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="simple-email-input"
                  className="mt-3 border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus:border-stone-900 bg-transparent" />
              </div>

              <div>
                <Label htmlFor="message" className="text-xs tracking-wider uppercase text-stone-500 font-medium">Mensagem</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} data-testid="simple-message-input"
                  className="mt-3 border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus:border-stone-900 bg-transparent resize-none" />
              </div>

              <Button type="submit" disabled={isSubmitting} data-testid="simple-submit-button"
                className="w-full bg-stone-900 hover:bg-stone-800 text-white font-medium py-6 text-sm tracking-wide rounded-none mt-8 min-h-[52px]">
                {isSubmitting ? 'A enviar...' : 'Enviar mensagem'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
